/**
 * Pure helpers for the public (no-login) compute funding page.
 *
 * Everything here is presentation-only formatting over values the server has
 * already decided. Amounts arrive as DECIMAL STRINGS and stay strings: no
 * helper in this file converts an asset amount to a float, so the exact
 * amount the server asked for is the exact amount the page shows and copies.
 *
 * (Named ...Model rather than a case-variant of the page file: this repo is
 * checked out on case-insensitive filesystems, where `publicFundingRequest.js`
 * and `PublicFundingRequest.js` would be the same file.)
 */

/** Statuses after which nothing on the page can change any more. */
export const TERMINAL_FUNDING_STATUSES = Object.freeze([
  "funded",
  "overfunded",
  "cancelled",
  "superseded",
  "expired",
]);

export const FUNDING_STATUS_POLL_INTERVAL_MS = 30_000;

export const isTerminalFundingStatus = (status) =>
  TERMINAL_FUNDING_STATUSES.includes(String(status || "").toLowerCase());

/**
 * The colour family a status pill reads in. Tones, not colours: the
 * stylesheet owns the palette.
 *   awaiting_payment                  neutral
 *   partially_funded                  amber
 *   funded / overfunded               green
 *   cancelled / superseded / expired  muted
 */
export const statusTone = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "partially_funded":
      return "amber";
    case "funded":
    case "overfunded":
      return "green";
    case "cancelled":
    case "superseded":
    case "expired":
      return "muted";
    default:
      return "neutral";
  }
};

/** `${apiBase}/api/public/funding-requests/${token}` — the one public base. */
export const buildPublicFundingBase = (apiBase, token) =>
  `${String(apiBase || "").replace(/\/+$/, "")}/api/public/funding-requests/${encodeURIComponent(
    String(token || "")
  )}`;

const DECIMAL_STRING = /^-?\d+(?:\.\d+)?$/;

const groupThousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/** Add one to a non-negative digit string without ever touching a Number. */
const incrementDigits = (digits) => {
  const out = digits.split("");
  let index = out.length - 1;
  while (index >= 0) {
    if (out[index] === "9") {
      out[index] = "0";
      index -= 1;
    } else {
      out[index] = String(Number(out[index]) + 1);
      return out.join("");
    }
  }
  return `1${out.join("")}`;
};

/**
 * "10000.00" → "$10,000.00". Always two decimals, always grouped. Anything
 * that is not a decimal string renders as an em dash rather than "$NaN".
 */
export const formatUsd = (value) => {
  const raw = String(value ?? "").trim();
  if (!DECIMAL_STRING.test(raw)) return "—";
  const negative = raw.startsWith("-");
  const [whole, fraction = ""] = raw.replace(/^-/, "").split(".");
  // Round half-up to cents on the string so a 3+ decimal USD value cannot
  // drift through float arithmetic.
  let cents = (fraction + "00").slice(0, 2);
  let integer = whole;
  if (fraction.length > 2 && Number(fraction[2]) >= 5) {
    const bumped = incrementDigits(whole + cents).padStart(3, "0");
    integer = bumped.slice(0, -2);
    cents = bumped.slice(-2);
  }
  return `${negative ? "-" : ""}$${groupThousands(integer)}.${cents}`;
};

/**
 * "3200" + "XRP" → "3,200 XRP"; "0.5" + "XRP" → "0.5 XRP". The fractional
 * part is preserved exactly as the server sent it (trailing zeros trimmed),
 * because the asset amount is what the payer must send.
 */
export const formatAssetAmount = (value, asset = "") => {
  const raw = String(value ?? "").trim();
  if (!DECIMAL_STRING.test(raw)) return asset ? `— ${asset}` : "—";
  const negative = raw.startsWith("-");
  const [whole, fraction = ""] = raw.replace(/^-/, "").split(".");
  const trimmedFraction = fraction.replace(/0+$/, "");
  const number = `${negative ? "-" : ""}${groupThousands(whole)}${
    trimmedFraction ? `.${trimmedFraction}` : ""
  }`;
  const unit = String(asset || "").trim();
  return unit ? `${number} ${unit}` : number;
};

/** True when a decimal-string amount is present and not zero. */
export const isNonZeroAmount = (value) => {
  const raw = String(value ?? "").trim();
  if (!DECIMAL_STRING.test(raw)) return false;
  return /[1-9]/.test(raw);
};

/** ISO → "Aug 24, 2026, 2:05 PM UTC", or null when unparseable. */
export const formatIssuedAt = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  }).format(date);
};

/** "XRP Ledger · mainnet" — label first, chain network second, either optional. */
export const formatNetworkLine = (networkLabel, chainNetwork) =>
  [networkLabel, chainNetwork]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" · ");

/* ------------------------------------------------------------------ *
 * Quotes (USD-denominated requests)
 *
 * A USD request is paid in a volatile asset at a server-issued quote that
 * expires. The page shows the quote's asset amount EXACTLY as the server
 * sent it — that string is what the payment URI / QR encodes and what the
 * payer copies — and counts the quote down from `secondsRemaining`. When it
 * reaches zero the page hides the amount and re-fetches; the server issues
 * the next quote on its own.
 * ------------------------------------------------------------------ */

export const QUOTE_EXPIRED_REFRESHING_LABEL = "QUOTE EXPIRED · REFRESHING PRICE…";
export const QUOTE_AWAITING_QR_COPY = "Payment QR will appear when a current quote is available";
export const QUOTE_TICK_INTERVAL_MS = 1_000;
/** Below this many seconds a volatile quote's countdown reads as urgent. */
export const QUOTE_URGENT_SECONDS = 30;

export const isUsdDenomination = (fundingRequest) =>
  String(fundingRequest?.denomination || "").toLowerCase() === "usd";

/** "151" | 151 → 151; anything that is not a finite non-negative number → null. */
const toSeconds = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) return null;
  return number;
};

/** 151 → "02:31"; 0, negatives and non-numbers → "00:00"; 3725 → "62:05". */
export const formatCountdown = (seconds) => {
  const total = Math.max(0, Math.ceil(toSeconds(seconds) ?? 0));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
};

/**
 * A quote the page may show and encode: an object the server marked valid
 * (or did not mark invalid) with a decimal-string asset amount and, when a
 * `secondsRemaining` is given, time still on it.
 */
export const quoteIsUsable = (quote) => {
  if (!quote || typeof quote !== "object") return false;
  if (quote.valid === false) return false;
  if (!DECIMAL_STRING.test(String(quote.assetAmount ?? "").trim())) return false;
  const remaining = toSeconds(quote.secondsRemaining);
  if (quote.secondsRemaining !== undefined && quote.secondsRemaining !== null && remaining === null) {
    return false;
  }
  return remaining === null || remaining > 0;
};

export const isStablecoinParQuote = (quote) =>
  String(quote?.basis || "").toLowerCase() === "stablecoin_par";

/**
 * The instant (ms) at which a quote received at `receivedAtMs` runs out, or
 * null when the server gave no `secondsRemaining`. The countdown is anchored
 * to the moment the response arrived so a slow request cannot lend a quote
 * time it does not have.
 */
export const quoteDeadlineMs = (quote, receivedAtMs) => {
  if (!quote || typeof quote !== "object") return null;
  const remaining = toSeconds(quote.secondsRemaining);
  if (remaining === null) return null;
  return receivedAtMs + remaining * 1000;
};

/** Whole seconds left on a deadline, never negative; null when no deadline. */
export const quoteSecondsLeft = (deadlineMs, nowMs) => {
  if (deadlineMs === null || deadlineMs === undefined || !Number.isFinite(deadlineMs)) return null;
  return Math.max(0, Math.ceil((deadlineMs - nowMs) / 1000));
};

/** ISO → "just now" | "45 seconds ago" | "2 minutes ago" | "1 hour ago"; null when unparseable. */
export const formatRelativeTime = (iso, nowMs) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const seconds = Math.floor((nowMs - then) / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds} seconds ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

/**
 * The quote's asset amount for display: the server's string, untouched. No
 * grouping, no trimming — what is shown is byte-for-byte what is encoded.
 */
export const formatQuoteAmount = (quote, asset = "") => {
  const raw = String(quote?.assetAmount ?? "").trim();
  if (!DECIMAL_STRING.test(raw)) return asset ? `— ${asset}` : "—";
  const unit = String(asset || "").trim();
  return unit ? `${raw} ${unit}` : raw;
};

/**
 * The unit line under the quote: the server's `unitLabel` verbatim, or for a
 * stablecoin at par "Pegged 1 USDC = $1.00".
 */
export const quoteUnitLine = (quote, asset = "") => {
  if (!quote || typeof quote !== "object") return "";
  const unitLabel = String(quote.unitLabel || "").trim();
  if (isStablecoinParQuote(quote)) {
    if (unitLabel) return `Pegged ${unitLabel}`;
    const peg = formatUsd(quote.pegUsd ?? "1");
    const unit = String(asset || "").trim();
    return unit && peg !== "—" ? `Pegged 1 ${unit} = ${peg}` : "";
  }
  return unitLabel;
};

/** "Price via coingecko · quoted 2 minutes ago" — either half optional. */
export const quoteProvenanceLine = (quote, nowMs) => {
  if (!quote || typeof quote !== "object") return "";
  const source = String(quote.priceSource || "").trim();
  const quoted = formatRelativeTime(quote.quotedAt, nowMs);
  const parts = [];
  if (source) parts.push(`Price via ${source}`);
  if (quoted) parts.push(source ? `quoted ${quoted}` : `Quoted ${quoted}`);
  return parts.join(" · ");
};

/** "XRP · XRP Ledger (mainnet)" — every part optional. */
export const formatPayWithLine = (asset, networkLabel, chainNetwork) => {
  const unit = String(asset || "").trim();
  const label = String(networkLabel || "").trim();
  const chain = String(chainNetwork || "").trim();
  const network = label && chain ? `${label} (${chain})` : label || chain;
  return [unit, network].filter(Boolean).join(" · ");
};

const QUOTE_UNAVAILABLE_STATES = Object.freeze([
  "unavailable",
  "price_stale",
  "price_unavailable",
  "settled",
  "not_applicable",
]);

/**
 * What to show in the quote slot when there is nothing to show. The server's
 * `quoteUnavailableLabel` verbatim when it has said the price is unavailable
 * or stale (or the request no longer needs a quote); otherwise the quote has
 * simply expired and the page is fetching the next one.
 */
export const quoteUnavailableCopy = (fundingRequest) => {
  const state = String(fundingRequest?.quoteState || "").toLowerCase();
  const label = String(fundingRequest?.quoteUnavailableLabel || "").trim();
  if (label && QUOTE_UNAVAILABLE_STATES.includes(state)) return label;
  return QUOTE_EXPIRED_REFRESHING_LABEL;
};

/* ------------------------------------------------------------------ *
 * Allow-lists — the page only ever holds fields from the public contract
 * ------------------------------------------------------------------ */

const pick = (source, keys) => {
  if (!source || typeof source !== "object") return null;
  const out = {};
  keys.forEach((key) => {
    if (source[key] !== undefined) out[key] = source[key];
  });
  return out;
};

const QUOTE_KEYS = Object.freeze([
  "quoteId",
  "assetAmount",
  "marketPrice",
  "priceSource",
  "priceTimestamp",
  "quotedAt",
  "expiresAt",
  "secondsRemaining",
  "basis",
  "pegUsd",
  "assetDecimals",
  "valid",
  "unitLabel",
]);

const PAYMENT_KEYS = Object.freeze([
  "address",
  "referenceKind",
  "referenceLabel",
  "referenceValue",
  "referenceRequired",
  "qrPayload",
  "qrPayloadType",
  "qrAmountUnit",
  "addressQrPayload",
  "addressQrPayloadType",
  "paymentUri",
  "walletDeepLink",
  "amount",
  "amountState",
  "qrImageUrl",
  "asset",
  "network",
  "chainNetwork",
]);

/** The payment fields a `/status` response may replace. Never the destination.
 *  The payment QR carries the amount, so a refreshed quote must be able to
 *  replace it — but `addressQrPayload` is the destination itself and is
 *  deliberately absent here, so no quote can ever move it. */
const PAYMENT_STATUS_KEYS = Object.freeze([
  "amount",
  "amountState",
  "qrPayload",
  "qrPayloadType",
  "qrAmountUnit",
  "paymentUri",
  "walletDeepLink",
]);

const FEE_POLICY_KEYS = Object.freeze(["cphereFeeLabel", "networkFeeLabel"]);

const FUNDING_REQUEST_KEYS = Object.freeze([
  "requestKind",
  "requestTitle",
  "amountLabel",
  "advertiserName",
  "advertiserLogoUrl",
  "advertiserLogoAltText",
  "campaignName",
  "requestedAmountUsd",
  "settlementCurrency",
  "requestedAmount",
  "asset",
  "tokenStandard",
  "network",
  "chainNetwork",
  "networkLabel",
  "assetLabel",
  "status",
  "statusLabel",
  "receivedAmount",
  "remainingAmount",
  "overpaidAmount",
  "denomination",
  "settledUsd",
  "remainingUsd",
  "overpaymentUsd",
  "quoteState",
  "quoteUnavailableReason",
  "quoteUnavailableLabel",
  "instructions",
  "issuedAt",
  "expiresAt",
  // `recipientName` is deliberately NOT here. The identity block names the
  // advertiser and the campaign; the person the link was sent to is not
  // printed on the page, so their name is dropped on arrival rather than
  // carried in page state where a future edit could render it.
  "readOnlyLedger",
  "safetyCopy",
  "matchedPaymentCount",
  "updatedAt",
]);

/** Top-level fields a `/status` response may replace. */
const STATUS_KEYS = Object.freeze([
  "status",
  "statusLabel",
  "receivedAmount",
  "remainingAmount",
  "overpaidAmount",
  "matchedPaymentCount",
  "updatedAt",
  "settledUsd",
  "remainingUsd",
  "overpaymentUsd",
  "quoteState",
  "quoteUnavailableReason",
  "quoteUnavailableLabel",
  "instructions",
]);

const sanitizeQuote = (quote) => (quote && typeof quote === "object" ? pick(quote, QUOTE_KEYS) : null);

const sanitizeInstructions = (instructions) =>
  Array.isArray(instructions)
    ? instructions.filter((line) => typeof line === "string" && line.trim())
    : [];

/**
 * The public GET /:token payload reduced to the contract's fields. Anything
 * else the server (or a proxy, or a mock) might attach is dropped here, so
 * nothing outside the contract can reach the DOM.
 */
export const sanitizePublicFundingRequest = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const out = pick(raw, FUNDING_REQUEST_KEYS);
  out.payment = pick(raw.payment, PAYMENT_KEYS) || {};
  out.feePolicy = pick(raw.feePolicy, FEE_POLICY_KEYS) || {};
  out.instructions = sanitizeInstructions(raw.instructions);
  out.quote = sanitizeQuote(raw.quote);
  return out;
};

/**
 * Fold a GET /:token/status body into the current request. Only the fields
 * the status endpoint is allowed to move are taken; the destination address,
 * the reference and the network are the initial payload's for the life of
 * the page. A `quote` key present in the body (even null) replaces the
 * current quote; an absent key leaves it alone.
 */
export const mergeFundingStatus = (current, body) => {
  if (!current || typeof current !== "object") return current;
  if (!body || typeof body !== "object") return current;
  const next = { ...current, ...pick(body, STATUS_KEYS) };
  if (body.instructions !== undefined) next.instructions = sanitizeInstructions(body.instructions);
  if (body.quote !== undefined) next.quote = sanitizeQuote(body.quote);
  if (body.payment && typeof body.payment === "object") {
    next.payment = { ...(current.payment || {}), ...pick(body.payment, PAYMENT_STATUS_KEYS) };
  }
  return next;
};
